// src/components/Grammar/Grammar.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Grammar.css';

const Grammar: React.FC = () => {
  const navigate = useNavigate();
  
  // Helper functions to get activity colors - matching Story Creator Studio theme
  const getActivityColor = (className: string) => {
    const colorMap: { [key: string]: string } = {
      'flashcards': '#3498DB',
      'detective': '#9B59B6', 
      'detective-advanced': '#E91E63',
      'defender': '#2ECC71'
    };
    return colorMap[className] || '#3498DB';
  };

  const getActivityGradient = (className: string) => {
    const gradientMap: { [key: string]: string } = {
      'flashcards': 'linear-gradient(135deg, #3498DB, #2980B9)',
      'detective': 'linear-gradient(135deg, #9B59B6, #8E44AD)',
      'detective-advanced': 'linear-gradient(135deg, #E91E63, #C2185B)',
      'defender': 'linear-gradient(135deg, #2ECC71, #27AE60)'
    };
    return gradientMap[className] || 'linear-gradient(135deg, #3498DB, #2980B9)';
  };
  
  const activities = [
    {
      id: 'flashcards',
      title: 'Grammar Flashcards',
      description: 'Learn grammar rules through interactive flashcards. Test your knowledge of parts of speech, sentence structure, and punctuation.',
      icon: '✏️',
      onClick: () => navigate('/grammar/flashcards'),
      className: 'flashcards',
      xp: '+15 XP per set',
      status: '3 sets available'
    },
    {
      id: 'detective',
      title: 'Grammar Detective',
      description: 'Become a grammar detective! Find and fix grammar errors in sentences and paragraphs. Perfect for improving proofreading skills.',
      icon: '🔍',
      onClick: () => navigate('/grammar/detective'),
      className: 'detective',
      xp: '+20 XP per level',
      status: '5 levels unlocked'
    },
    {
      id: 'advanced-detective',
      title: 'Advanced Detective',
      description: 'Take your grammar detective skills to the next level with complex texts and challenging grammatical concepts.',
      icon: '🕵️‍♂️',
      onClick: () => navigate('/grammar/advanced-detective'),
      className: 'detective-advanced',
      xp: '+30 XP complete',
      status: 'Challenge mode'
    },
    {
      id: 'defender',
      title: 'Grammar Defender',
      description: 'Save Earth from the grammar comet! Use your grammar knowledge to power the deflector shields and protect our planet in this exciting space adventure.',
      icon: '🚀',
      onClick: () => navigate('/grammar/defender'),
      className: 'defender',
      xp: '+25 XP complete',
      status: 'Space adventure'
    }
  ];

  return (
    <div className="grammar-container">
      {/* Header with Reward Tracking - matching Story Creator Studio */}
      <div className="grammar-header">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h2>Grammar Mastery Center</h2>
            <div className="grammar-level-info">
              <div className="grammar-level-badge">
                <span>👑</span>
                Level 2 Grammar Expert
              </div>
              <div className="grammar-xp-info">
                120/200 XP to Level 3
              </div>
            </div>
          </div>
          
          <button style={{
            background: 'linear-gradient(45deg, #E91E63, #9B59B6)',
            color: 'white',
            border: 'none',
            padding: '16px 28px',
            borderRadius: '20px',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
          }}
          >
            🎁 Grammar Rewards
          </button>
        </div>

        <div className="grammar-xp-bar">
          <div className="grammar-xp-progress"></div>
        </div>

        <div className="grammar-stats-grid">
          <div className="grammar-stat-card exercises">
            <div className="grammar-stat-number">47</div>
            <div className="grammar-stat-label">Exercises Completed</div>
          </div>
          
          <div className="grammar-stat-card accuracy">
            <div className="grammar-stat-number">92%</div>
            <div className="grammar-stat-label">Accuracy Rate</div>
          </div>
          
          <div className="grammar-stat-card streak fire">
            <div className="grammar-stat-number">8</div>
            <div className="grammar-stat-label">Day Streak</div>
          </div>
        </div>
      </div>

      {/* Activity Cards - matching Story Creator Studio structure exactly */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: '32px',
        marginBottom: '40px'
      }}>
        {activities.map((activity, index) => (
          <div key={activity.id} 
            onClick={activity.onClick}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              border: `4px solid ${getActivityColor(activity.className)}`,
              boxShadow: '0 15px 50px rgba(0,0,0,0.15)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 25px 60px rgba(0,0,0,0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 15px 50px rgba(0,0,0,0.15)';
            }}
          >
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '120px',
              height: '120px',
              background: getActivityGradient(activity.className),
              borderRadius: '50%',
              opacity: 0.1
            }} />
            
            {/* XP Reward Badge */}
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: getActivityGradient(activity.className),
              color: 'white',
              borderRadius: '16px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              fontWeight: '700',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              {activity.xp}
            </div>
            
            <div style={{
              width: '80px',
              height: '80px',
              background: getActivityGradient(activity.className),
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              color: 'white',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              fontSize: '40px',
              transition: 'all 0.3s ease'
            }}>
              {activity.icon}
            </div>
            
            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '800',
              color: '#333',
              margin: '0 0 16px 0'
            }}>
              {activity.title}
            </h3>
            
            <p style={{
              color: '#666',
              fontSize: '1.1rem',
              lineHeight: '1.6',
              margin: '0 0 24px 0'
            }}>
              {activity.description}
            </p>
            
            <div style={{
              background: `${getActivityColor(activity.className)}15`,
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '1rem',
              fontWeight: '600',
              color: getActivityColor(activity.className),
              display: 'inline-block',
              border: `2px solid ${getActivityColor(activity.className)}30`
            }}>
              {activity.status}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Grammar;